import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/supabaseService';
import { storageService } from '../../services/storageService';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

const DashboardSettingsPage = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [coverLoading, setCoverLoading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [coverError, setCoverError] = useState('');
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const avatarUrl = userProfile?.avatarUrl ?? userProfile?.avatar_url;
  const bannerUrl = userProfile?.bannerUrl ?? userProfile?.banner_url;

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setAvatarError('');
    setAvatarLoading(true);
    try {
      const url = await storageService.uploadAvatar(user.id, file);
      await profileService.updateProfileMedia(user.id, { avatar_url: url });
      await refreshProfile();
    } catch (err) {
      setAvatarError(err.message || 'Upload failed');
    } finally {
      setAvatarLoading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id) return;
    setAvatarError('');
    setAvatarLoading(true);
    try {
      await storageService.removeAvatar(user.id);
      await profileService.updateProfileMedia(user.id, { avatar_url: null });
      await refreshProfile();
    } catch (err) {
      setAvatarError(err.message || 'Remove failed');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setCoverError('');
    setCoverLoading(true);
    try {
      const url = await storageService.uploadCover(user.id, file);
      await profileService.updateProfileMedia(user.id, { banner_url: url });
      await refreshProfile();
    } catch (err) {
      setCoverError(err.message || 'Upload failed');
    } finally {
      setCoverLoading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleRemoveCover = async () => {
    if (!user?.id) return;
    setCoverError('');
    setCoverLoading(true);
    try {
      await storageService.removeCover(user.id);
      await profileService.updateProfileMedia(user.id, { banner_url: null });
      await refreshProfile();
    } catch (err) {
      setCoverError(err.message || 'Remove failed');
    } finally {
      setCoverLoading(false);
    }
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-white mb-4">Settings</h1>

      <div className="p-6 rounded-2xl bg-slate-800/50 border border-white/10 space-y-4 mb-6">
        <p className="text-slate-300">
          <span className="font-medium text-slate-200">Email:</span> {user?.email}
        </p>
        <p className="text-slate-300">
          <span className="font-medium text-slate-200">Display name:</span>{' '}
          {userProfile?.displayName ?? userProfile?.display_name ?? '—'}
        </p>
      </div>

      <div className="space-y-8">
        <section className="p-6 rounded-2xl bg-slate-800/50 border border-white/10">
          <h2 className="font-heading font-semibold text-lg text-white mb-3">My Subscription</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-900/50 border border-white/5">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    userProfile?.subscriptionStatus === 'premium' 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/20' 
                    : 'bg-slate-700 text-slate-400'
                  }`}>
                    {userProfile?.subscriptionStatus === 'premium' ? 'Premium Active' : 'Free Account'}
                  </span>
                </div>
              </div>
              {userProfile?.subscriptionStatus === 'premium' && userProfile?.subscriptionEndAt && (
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-1">Expires On</p>
                  <p className="text-sm text-white font-mono">
                    {new Date(userProfile.subscriptionEndAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>

            {userProfile?.subscriptionStatus !== 'premium' && (
              <button
                onClick={() => window.location.href = '/payment'}
                className="w-full py-3 rounded-xl bg-brand-pink text-white font-bold hover:bg-brand-pink-dark transition-all shadow-lg shadow-brand-pink/20"
              >
                Upgrade Now
              </button>
            )}
          </div>
        </section>

        <section className="p-6 rounded-2xl bg-slate-800/50 border border-white/10">
          <h2 className="font-heading font-semibold text-lg text-white mb-3">Profile photo (avatar)</h2>
          <p className="text-slate-400 text-sm mb-4">JPEG, PNG or WebP, max 2MB.</p>
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-24 h-24 rounded-xl bg-slate-700 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-10 h-10 text-slate-500" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-pink text-white text-sm font-medium hover:bg-brand-pink-dark disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {avatarLoading ? 'Uploading...' : 'Upload photo'}
              </button>
              {avatarUrl && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  disabled={avatarLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/20 text-slate-300 text-sm font-medium hover:bg-white/10 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  Remove
                </button>
              )}
            </div>
          </div>
          {avatarError && <p className="mt-2 text-sm text-red-400">{avatarError}</p>}
        </section>

        <section className="p-6 rounded-2xl bg-slate-800/50 border border-white/10">
          <h2 className="font-heading font-semibold text-lg text-white mb-3">Cover (banner)</h2>
          <p className="text-slate-400 text-sm mb-4">Optional. JPEG, PNG or WebP, max 2MB.</p>
          <div className="flex flex-col gap-4">
            <div className="w-full max-w-md h-32 rounded-xl bg-slate-700 overflow-hidden flex items-center justify-center">
              {bannerUrl ? (
                <img src={bannerUrl} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-12 h-12 text-slate-500" />
              )}
            </div>
            <div className="flex gap-2">
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleCoverChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={coverLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-pink text-white text-sm font-medium hover:bg-brand-pink-dark disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {coverLoading ? 'Uploading...' : 'Upload cover'}
              </button>
              {bannerUrl && (
                <button
                  type="button"
                  onClick={handleRemoveCover}
                  disabled={coverLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/20 text-slate-300 text-sm font-medium hover:bg-white/10 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  Remove
                </button>
              )}
            </div>
          </div>
          {coverError && <p className="mt-2 text-sm text-red-400">{coverError}</p>}
        </section>
      </div>
    </div>
  );
};

export default DashboardSettingsPage;
